import React from "react";

import {
  Sparkles,
  MessageCircle,
  Phone,
  Mail,
  ArrowUpRight,
} from "lucide-react";

import {
  WHATSAPP_URL,
  TEL_URL,
  PHONE,
} from "@/lib/brand";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[var(--gf-primary)] text-white relative overflow-hidden"
    >

      {/* Background Glow */}
      <div className="absolute top-0 right-0 h-[320px] w-[320px] bg-[var(--gf-blue)]/20 blur-3xl rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 relative">

        {/* Top CTA */}
        <div className="mb-16 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">

          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[var(--gf-blue)] font-semibold">
              Ready to grow?
            </p>

            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold leading-tight max-w-2xl">
              Let's build your business presence online.
            </h2>

            <p className="mt-4 text-white/70 max-w-xl leading-relaxed">
              Websites, SEO, Google Ads & WhatsApp automation —
              everything your business needs to attract more customers.
            </p>
          </div>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[var(--gf-green)] hover:bg-[var(--gf-green-deep)] text-white font-semibold h-14 px-7 rounded-full transition-all hover:-translate-y-0.5 shadow-xl"
          >
            <MessageCircle className="h-5 w-5" />

            Chat on WhatsApp

            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        {/* Main Footer */}
        <div className="grid md:grid-cols-12 gap-12">

          {/* Brand */}
          <div className="md:col-span-5">

            <div className="flex items-center gap-3">

              <span className="h-11 w-11 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shadow-lg">
                <Sparkles className="h-5 w-5" />
              </span>

              <div>
                <span className="font-display text-2xl font-extrabold">
                  Grow<span className="text-[var(--gf-blue)]">Flow</span>
                </span>

                <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-1">
                  Digital Growth Agency
                </p>
              </div>
            </div>

            <p className="mt-6 text-sm text-white/70 max-w-md leading-relaxed">
              GrowFlow helps clinics, gyms, restaurants and local
              businesses grow online through premium websites,
              SEO, Google Ads and conversion-focused digital experiences.
            </p>

            {/* Small Trust Tags */}
            <div className="mt-6 flex flex-wrap gap-3">

              {[
                "SEO Optimized",
                "Mobile First",
                "Fast Support",
              ].map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/70"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-3">

            <p className="text-xs uppercase tracking-[0.22em] font-bold text-white/50">
              Quick Links
            </p>

            <ul className="mt-5 space-y-3 text-sm">

              <li>
                <a
                  href="#services"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  Services
                </a>
              </li>

              <li>
                <a
                  href="#pricing"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  Pricing
                </a>
              </li>

              <li>
                <a
                  href="#how"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  How It Works
                </a>
              </li>

              <li>
                <a
                  href="#lead-form"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  Free Consultation
                </a>
              </li>

            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">

            <p className="text-xs uppercase tracking-[0.22em] font-bold text-white/50">
              Contact
            </p>

            <ul className="mt-5 space-y-4 text-sm">

              <li>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <span className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <MessageCircle className="h-4 w-4 text-[var(--gf-green)]" />
                  </span>

                  WhatsApp: {PHONE}
                </a>
              </li>

              <li>
                <a
                  href={TEL_URL}
                  className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <span className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <Phone className="h-4 w-4 text-[var(--gf-blue)]" />
                  </span>

                  Call: {PHONE}
                </a>
              </li>

              <li className="inline-flex items-center gap-3 text-white/80">
                <span className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-white/70" />
                </span>

                hello@growflow.in
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">

          <p>
            © {new Date().getFullYear()} GrowFlow. All rights reserved.
          </p>

          <p>
            Designed for modern local businesses in India.
          </p>
        </div>
      </div>
    </footer>
  );
}
